import { useCallback, useEffect, useRef, useState } from "react";
import styles from "./usePageTransition.module.css";

const EXIT_DURATION_MS = 300;

/**
 * Hook for page enter/exit transitions.
 *
 * Returns:
 *  - `transitionClass`: CSS class to apply to the page wrapper
 *  - `exitThen`: triggers exit animation, then calls the provided callback after the animation completes
 */
function usePageTransition({ enter = true } = {}) {
  const [isExiting, setIsExiting] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const transitionClass = isExiting ? styles.exit : enter ? styles.enter : "";

  const exitThen = useCallback((callback: () => void) => {
    setIsExiting(true);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      callback();
    }, EXIT_DURATION_MS);
  }, []);

  return { transitionClass, exitThen };
}

export { usePageTransition };
